import { NgClass, NgStyle } from '@angular/common';
import {
    AfterViewInit,
    Component,
    ComponentRef,
    HostListener,
    inject,
    Injector,
    OnDestroy,
    Signal,
    StaticProvider,
    Type,
    ViewChild,
    viewChildren,
    ViewContainerRef
} from '@angular/core';
import { IconKey } from '@common/classes/icons';
import { ButtonComponent } from '@common/components/button/button.component';
import { BaseInputComponent } from '@common/components/input/base-input/base-input.component';
import { InputComponent } from '@common/components/input/input.component';
import {
    BooleanInputConfig,
    CodelistInputConfig,
    DateInputConfig,
    DateRangeInputConfig,
    DateTimeInputConfig,
    EditorInputConfig,
    FileInputConfig,
    InputConfig,
    InputType,
    NumberInputConfig,
    SelectInputConfig,
    TextareaInputConfig
} from '@common/components/input/input.type';
import { ToastrNotificationService } from '@common/services/toastr-notification.service';
import { environment } from '@environments/environment';
import { NgIcon } from '@ng-icons/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { DateInputCustomFormatPlaceholder } from '@progress/kendo-angular-dateinputs';
import {
    DialogActionsComponent,
    DialogContentBase,
    DialogRef,
    DialogTitleBarComponent
} from '@progress/kendo-angular-dialog';
import _ from 'lodash';

export interface DialogFormOptions {
    title: string;
    titleIcons?: IconKey[];
    message?: string;
    model?: any;
    cancelText?: string;
    cancelIcon?: IconKey;
    cancelAction?: Function;
    confirmText?: string;
    confirmIcon?: IconKey;
    confirmMessage?: string;
    properties?: InputConfig[];
    canEdit?: (model: any) => boolean;
    canConfirm?: (model: any) => boolean;
    contentComponent?: Type<any>;
    contentComponentDataProviders?: StaticProvider[];
    onContentComponentCreated?: (component: any) => void;
}

@Component({
    selector: 'app-dialog-form',
    templateUrl: './dialog-form.component.html',
    imports: [
        DialogTitleBarComponent,
        NgIcon,
        TranslatePipe,
        NgClass,
        DialogActionsComponent,
        NgStyle,
        ButtonComponent,
        InputComponent
    ]
})
export class DialogFormComponent extends DialogContentBase implements OnDestroy, AfterViewInit {
    private toastrNotificationService = inject(ToastrNotificationService);
    private translateService = inject(TranslateService);

    @ViewChild('template', { static: false, read: ViewContainerRef }) templateRef: ViewContainerRef;

    private contentComponentRef: ComponentRef<any>;
    private injector: Injector;

    model: any;
    options: DialogFormOptions = {
        title: 'Form',
        cancelText: 'Cancel',
        confirmText: 'Confirm'
    };
    confirmAction: Function;
    canConfirmPredicate: (model: any) => boolean = () => true;
    canEditPredicate: (model: any) => boolean = () => true;
    cancelAction: Function;

    inputs: Signal<readonly BaseInputComponent<any>[]> = viewChildren(BaseInputComponent);
    private fileRestrictions = environment.settings.appControl.fileRestrictions;
    private popupSettings = environment.settings.appControl.dropdown.popupSettings;
    private dateFormatPlaceholder: DateInputCustomFormatPlaceholder = {
        day: this.translateService.instant('day'),
        month: this.translateService.instant('month'),
        year: this.translateService.instant('year'),
        hour: this.translateService.instant('hour'),
        minute: this.translateService.instant('minute'),
        second: this.translateService.instant('second'),
        millisecond: this.translateService.instant('millisecond')
    };

    @HostListener('document:keyup.escape', ['$event']) onEscapePress() {
        this.close();
    }

    @HostListener('document:keyup.enter', ['$event']) onEnterPress() {
        if (!this.canConfirmPredicate(this.model)) return;
        this.confirmAction();
    }

    constructor() {
        super(inject(DialogRef));
    }

    public initialize(options: DialogFormOptions, injector: Injector): void {
        for (const [key, value] of Object.entries(options)) {
            if (value != null) this.options[key] = value;
        }

        if (this.options.cancelAction) this.cancelAction = this.options.cancelAction;
        this.model = this.options.model || {};
        this.options.properties?.forEach((prop) => (this.model[prop.id] = prop.initialValue));
        this.injector = injector;
        this.canConfirmPredicate = this.options.canConfirm || this.canConfirm.bind(this);
        this.canEditPredicate = this.options.canEdit || this.canEdit.bind(this);
    }

    confirm(): void {
        this.inputs().forEach((input) => input.validate());
        if (this.inputs().some((x) => x.error())) {
            this.toastrNotificationService.show({ type: 'error', message: 'Some required fields are empty' });
            return;
        }
        this.dialog.close(this.model);
    }

    canConfirm(_model: any): boolean {
        return true;
    }

    canEdit(_model: any): boolean {
        return true;
    }

    close(): void {
        this.dialog.close(null);
    }

    clear(): void {
        if (this.cancelAction) this.cancelAction();
        this.options.properties?.forEach((prop) => {
            prop.initialValue = null;
            this.model[this.getName(prop)] = null;
        });
    }

    private loadComponent(component, dataProviders: StaticProvider[]): void {
        dataProviders = (dataProviders || []).concat([{ provide: DialogRef, useValue: this.dialog }]);
        const injector = Injector.create({ providers: dataProviders || [], parent: this.injector });
        this.contentComponentRef = this.templateRef.createComponent(component, { injector });
        if (!this.contentComponentRef.instance.model) {
            this.contentComponentRef.instance.model = this.model;
        } else {
            this.model = this.contentComponentRef.instance.model;
        }

        if (_.isFunction(this.contentComponentRef.instance.confirm)) {
            this.confirmAction = () => {
                this.contentComponentRef.instance.confirm.bind(this.contentComponentRef.instance);
            };
        }

        if (_.isFunction(this.contentComponentRef.instance.canConfirm)) {
            this.canConfirmPredicate = this.contentComponentRef.instance.canConfirm.bind(
                this.contentComponentRef.instance
            );
        }

        if (_.isFunction(this.contentComponentRef.instance.canEdit)) {
            this.canEditPredicate = this.contentComponentRef.instance.canEdit.bind(this.contentComponentRef.instance);
        }

        if (_.isFunction(this.contentComponentRef.instance.cancel)) {
            this.cancelAction = this.contentComponentRef.instance.cancel.bind(this.contentComponentRef.instance);
        }

        if (_.isFunction(this.options.onContentComponentCreated)) {
            this.options.onContentComponentCreated(this.contentComponentRef.instance);
        }

        this.contentComponentRef.changeDetectorRef.detectChanges();
    }

    override ngAfterViewInit(): void {
        if (this.options.contentComponent) {
            this.loadComponent(this.options.contentComponent, this.options.contentComponentDataProviders);
        }

        this.confirmAction = this.confirmAction || this.confirm.bind(this);
        this.cancelAction = this.cancelAction || this.close.bind(this);
    }

    ngOnDestroy(): void {
        if (this.contentComponentRef) {
            this.contentComponentRef.destroy();
        }
    }

    getName(property: InputConfig): string {
        return property.id;
    }

    /**
     * Generates InputConfig for component app-input based on FormProperty
     * @param property - The FormProperty to generate config for
     * @returns InputConfig matching the property type
     */
    generateInputConfig(property: InputConfig): InputConfig {
        const baseConfig = {
            isEditMode: true,
            labelPosition: 'top',
            ...property
        } as InputConfig;

        switch (property.type) {
            case InputType.TEXT:
                return {
                    ...baseConfig,
                    maxLength: property.maxLength || 255
                } as TextareaInputConfig;
            case InputType.TEXTAREA:
                return {
                    ...baseConfig,
                    maxLength: property.maxLength || 255,
                    rows: property.rows || 3
                } as TextareaInputConfig;
            case InputType.NUMBER:
                return {
                    ...baseConfig,
                    min: property.min || 0,
                    format: property.format || '#',
                    decimals: property.decimals || 2
                } as NumberInputConfig;
            case InputType.BOOLEAN:
                return {
                    ...baseConfig
                } as BooleanInputConfig;
            case InputType.DATE:
                return {
                    ...baseConfig,
                    formatPlaceholder: property.formatPlaceholder || this.dateFormatPlaceholder
                } as DateInputConfig;
            case InputType.DATETIME:
                return {
                    ...baseConfig,
                    formatPlaceholder: property.formatPlaceholder || this.dateFormatPlaceholder
                } as DateTimeInputConfig;
            case InputType.DATERANGE:
                return {
                    ...baseConfig,
                    formatPlaceholder: property.formatPlaceholder || this.dateFormatPlaceholder
                } as DateRangeInputConfig;
            case InputType.FILE:
                return {
                    ...baseConfig,
                    showFileList: true,
                    restrictions: property.restrictions || this.fileRestrictions
                } as FileInputConfig;
            case InputType.SELECT:
                return {
                    ...baseConfig,
                    valuePrimitive: true,
                    popupSettings: property.popupSettings || this.popupSettings,
                    textField: 'label',
                    valueField: 'value'
                } as SelectInputConfig;
            case InputType.CODELIST:
                return {
                    ...baseConfig,
                    popupSettings: property.popupSettings || this.popupSettings,
                    textField: 'label',
                    valueField: 'value'
                } as CodelistInputConfig;
            case InputType.EDITOR:
                return {
                    ...baseConfig
                } as EditorInputConfig;
        }
    }
}
